import React, { useState, useEffect, useRef } from 'react';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CptSuggestionsPanel } from '../components/clinical/CptSuggestionsPanel';
import { DocStrengthMeter } from '../components/brand/DocStrengthMeter';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Activity, Moon, Sun, FileText, Plus, Search, User,
  Calendar, Clock, Save, Trash2, Edit, Eye, Filter,
  Stethoscope, Heart, Brain, Pill, AlertTriangle, CheckCircle,
  ClipboardList, MessageSquare, TrendingUp, ChevronRight,
  FileEdit, History, Lock, Unlock, Download, Sparkles, Copy, RefreshCw, Receipt,
  Mic, MicOff, Square, Volume2, HelpCircle, Command, Zap, Settings, PlusCircle, Layers,
} from 'lucide-react';
import { AIBrainLoader } from '../components/ui/ai-loading';

const API = process.env.REACT_APP_BACKEND_URL;

// SOAP Note Template
const SOAP_TEMPLATE = {
  subjective: '',
  objective: '',
  assessment: '',
  plan: ''
};

// Note Types
const NOTE_TYPES = [
  { value: 'soap', label: 'SOAP Note', icon: ClipboardList, description: 'Structured clinical documentation' },
  { value: 'progress', label: 'Progress Note', icon: TrendingUp, description: 'Patient progress update' },
  { value: 'visit', label: 'Visit Note', icon: Calendar, description: 'Visit documentation' },
  { value: 'assessment', label: 'Assessment Note', icon: Stethoscope, description: 'Clinical assessment' },
  { value: 'care_plan', label: 'Care Plan Note', icon: Heart, description: 'Care plan documentation' },
  { value: 'medication', label: 'Medication Note', icon: Pill, description: 'Medication changes' },
  { value: 'incident', label: 'Incident Report', icon: AlertTriangle, description: 'Incident documentation' }
];

export default function ClinicalNotes() {
  const { user, getAuthHeader } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewNote, setShowNewNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [viewingNote, setViewingNote] = useState(null);
  const [creatingClaim, setCreatingClaim] = useState(null);
  
  // AI Summary state
  const [showSummary, setShowSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);
  const [summaryType, setSummaryType] = useState('handoff');
  
  // Voice recording state
  const [showVoiceNote, setShowVoiceNote] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [voicePatientId, setVoicePatientId] = useState('');
  const [voiceNoteTitle, setVoiceNoteTitle] = useState('');
  const [voiceResult, setVoiceResult] = useState(null);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recognitionRef = useRef(null);
  
  // New note form
  const [newNote, setNewNote] = useState({
    patient_id: '',
    note_type: 'soap',
    title: '',
    content: '',
    soap: { ...SOAP_TEMPLATE },
    vitals: {
      blood_pressure: '',
      heart_rate: '',
      temperature: '',
      respiratory_rate: '',
      oxygen_saturation: '',
      weight: '',
      pain_level: ''
    },
    is_locked: false,
    tags: []
  });

  // AI-strengthen backup so the clinician can revert (inline Undo, lives inside the dialog).
  const [strengthenBackup, setStrengthenBackup] = useState(null);
  const applyStrengthened = (d) => {
    setStrengthenBackup(newNote);
    setNewNote(prev => ((d.soap || d.note_type === 'soap')
      ? { ...prev, soap: { ...prev.soap, ...(d.soap || {}) } }
      : { ...prev, content: d.content ?? prev.content }));
    const ph = (d.placeholders || []).length;
    toast.success(
      ph > 0
        ? `Note strengthened — fill in ${ph} "[ADD: …]" placeholder${ph > 1 ? 's' : ''} before saving.`
        : 'Note strengthened — review the changes before saving.',
    );
  };
  const undoStrengthen = () => {
    if (!strengthenBackup) return;
    setNewNote(strengthenBackup);
    setStrengthenBackup(null);
    toast.message('Reverted AI changes.');
  };

  // Auto-coding: turn a saved clinical note into a ready-to-review DRAFT claim.
  const navigate = useNavigate();
  const createClaimFromNote = async (note) => {
    setCreatingClaim(note.id);
    try {
      const { data } = await axios.post(
        `${API}/api/clinical-notes/${note.id}/create-claim`, {},
        { ...getAuthHeader(), timeout: 90000 },
      );
      const cpt = data.coding?.recommended_cpt;
      setViewingNote(null);
      toast.success(
        `Draft claim ${data.claim?.claim_number} created — coded ${cpt}. Review & submit in Billing.`,
        { action: { label: 'Open Billing', onClick: () => navigate('/clinician/billing') }, duration: 10000 },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create a claim from this note');
    } finally {
      setCreatingClaim(null);
    }
  };

  useEffect(() => {
    fetchNotes();
    fetchPatients();
  }, []);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/clinical-notes`, getAuthHeader());
      setNotes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      // Get clinician's matched patients
      const response = await axios.get(`${API}/api/clinician/matches`, getAuthHeader());
      const matchedPatients = response.data || [];
      
      // Get patient details
      const patientDetails = await Promise.all(
        matchedPatients
          .filter(m => m.status === 'accepted')
          .map(async (match) => {
            try {
              const patientResp = await axios.get(
                `${API}/api/admin/patients`,
                getAuthHeader()
              );
              return patientResp.data?.find(p => p.id === match.patient_id);
            } catch {
              return null;
            }
          })
      );
      
      setPatients(patientDetails.filter(Boolean));
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  const handleSaveNote = async () => {
    if (!newNote.patient_id || !newNote.title) {
      toast.error('Please select a patient and add a title');
      return;
    }

    setLoading(true);
    try {
      let content = newNote.content;
      
      // For SOAP notes, compile the content
      if (newNote.note_type === 'soap') {
        content = `SUBJECTIVE:\n${newNote.soap.subjective}\n\nOBJECTIVE:\n${newNote.soap.objective}\n\nASSESSMENT:\n${newNote.soap.assessment}\n\nPLAN:\n${newNote.soap.plan}`;
      }

      const noteData = {
        ...newNote,
        content,
        clinician_id: user.id
      };

      if (editingNote) {
        await axios.put(
          `${API}/api/clinical-notes/${editingNote.id}`,
          noteData,
          getAuthHeader()
        );
        toast.success('Note updated successfully');
      } else {
        await axios.post(`${API}/api/clinical-notes`, noteData, getAuthHeader());
        toast.success('Note created successfully');
      }

      setShowNewNote(false);
      setEditingNote(null);
      resetNewNote();
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save note');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;

    try {
      await axios.delete(`${API}/api/clinical-notes/${noteId}`, getAuthHeader());
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleLockNote = async (noteId, lock) => {
    try {
      await axios.put(
        `${API}/api/clinical-notes/${noteId}/lock`,
        { is_locked: lock },
        getAuthHeader()
      );
      toast.success(lock ? 'Note locked' : 'Note unlocked');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const resetNewNote = () => {
    setNewNote({
      patient_id: '',
      note_type: 'soap',
      title: '',
      content: '',
      soap: { ...SOAP_TEMPLATE },
      vitals: {
        blood_pressure: '',
        heart_rate: '',
        temperature: '',
        respiratory_rate: '',
        oxygen_saturation: '',
        weight: '',
        pain_level: ''
      },
      is_locked: false,
      tags: []
    });
    setStrengthenBackup(null);
  };

  const openEditNote = (note) => {
    setEditingNote(note);
    setNewNote({
      patient_id: note.patient_id,
      note_type: note.note_type,
      title: note.title,
      content: note.content,
      soap: note.soap || { ...SOAP_TEMPLATE },
      vitals: note.vitals || {},
      is_locked: note.is_locked,
      tags: note.tags || []
    });
    setShowNewNote(true);
  };

  // AI Summary Generation
  const generateSummary = async () => {
    if (!selectedPatient) {
      toast.error('Please select a patient first');
      return;
    }

    setSummaryLoading(true);
    setSummaryResult(null);
    
    try {
      const response = await axios.post(
        `${API}/api/clinical-notes/ai-summary`,
        {
          patient_id: selectedPatient,
          note_ids: [], // Empty means summarize all recent notes
          summary_type: summaryType
        },
        getAuthHeader()
      );
      
      setSummaryResult(response.data);
      toast.success('AI summary generated successfully');
    } catch (error) {
      console.error('Summary generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const copySummary = () => {
    if (summaryResult?.summary) {
      navigator.clipboard.writeText(summaryResult.summary);
      toast.success('Summary copied to clipboard');
    }
  };

  // Voice Recording Functions
  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      
      // Start real-time speech recognition
      startSpeechRecognition();
      
      setIsRecording(true);
      toast.info('Recording started... Speak your clinical notes');
    } catch (error) {
      toast.error('Could not access microphone');
      console.error('Recording error:', error);
    }
  };

  // Voice command definitions
  const VOICE_COMMANDS = {
    // SOAP Section commands
    'new section subjective': '\n\n📋 **SUBJECTIVE:**\n',
    'section subjective': '\n\n📋 **SUBJECTIVE:**\n',
    'subjective section': '\n\n📋 **SUBJECTIVE:**\n',
    'start subjective': '\n\n📋 **SUBJECTIVE:**\n',
    
    'new section objective': '\n\n🔬 **OBJECTIVE:**\n',
    'section objective': '\n\n🔬 **OBJECTIVE:**\n',
    'objective section': '\n\n🔬 **OBJECTIVE:**\n',
    'start objective': '\n\n🔬 **OBJECTIVE:**\n',
    
    'new section assessment': '\n\n🩺 **ASSESSMENT:**\n',
    'section assessment': '\n\n🩺 **ASSESSMENT:**\n',
    'assessment section': '\n\n🩺 **ASSESSMENT:**\n',
    'start assessment': '\n\n🩺 **ASSESSMENT:**\n',
    
    'new section plan': '\n\n📝 **PLAN:**\n',
    'section plan': '\n\n📝 **PLAN:**\n',
    'plan section': '\n\n📝 **PLAN:**\n',
    'start plan': '\n\n📝 **PLAN:**\n',
    
    // Formatting commands
    'new paragraph': '\n\n',
    'next paragraph': '\n\n',
    'new line': '\n',
    'next line': '\n',
    'bullet point': '\n• ',
    'new bullet': '\n• ',
    'dash': ' - ',
    
    // Punctuation commands
    'period': '.',
    'full stop': '.',
    'comma': ',',
    'question mark': '?',
    'exclamation point': '!',
    'exclamation mark': '!',
    'colon': ':',
    'semicolon': ';',
    'open parenthesis': ' (',
    'close parenthesis': ') ',
    'open bracket': ' [',
    'close bracket': '] ',
    
    // Medical shortcuts
    'vitals colon': '\n**Vitals:** ',
    'diagnosis colon': '\n**Diagnosis:** ',
    'medications colon': '\n**Medications:** ',
    'allergies colon': '\n**Allergies:** ',
    'history colon': '\n**History:** ',
    'chief complaint': '\n**Chief Complaint:** ',
    'follow up': '\n**Follow-up:** ',
    'patient reports': 'Patient reports ',
    'patient denies': 'Patient denies ',
    'no known allergies': 'No known allergies (NKA)',
  };

  const [lastCommand, setLastCommand] = useState(null);
  const [showCommandHelp, setShowCommandHelp] = useState(false);
  const [showCustomCommands, setShowCustomCommands] = useState(false);
  const [customCommands, setCustomCommands] = useState([]);
  const [commandTemplates, setCommandTemplates] = useState([]);
  const [newCustomCommand, setNewCustomCommand] = useState({ trigger_phrase: '', output_text: '', description: '' });
  const [editingCommand, setEditingCommand] = useState(null);
  const [loadingCommands, setLoadingCommands] = useState(false);

  // Fetch custom commands when voice note dialog opens
  useEffect(() => {
    if (showVoiceNote) {
      fetchCustomCommands();
      fetchCommandTemplates();
    }
  }, [showVoiceNote]);


  const fetchCustomCommands = async () => {
    try {
      const response = await axios.get(`${API}/api/voice/commands`, getAuthHeader());
      setCustomCommands(response.data);
    } catch (error) {
      console.error('Error fetching custom commands:', error);
    }
  };

  const fetchCommandTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/voice/commands/templates`, getAuthHeader());
      setCommandTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };


  const createCustomCommand = async () => {
    if (!newCustomCommand.trigger_phrase || !newCustomCommand.output_text) {
      toast.error('Please fill in trigger phrase and output text');
      return;
    }
    
    setLoadingCommands(true);
    try {
      await axios.post(`${API}/api/voice/commands`, newCustomCommand, getAuthHeader());
      toast.success('Custom command created!');
      setNewCustomCommand({ trigger_phrase: '', output_text: '', description: '' });
      fetchCustomCommands();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create command');
    } finally {
      setLoadingCommands(false);
    }
  };

  const deleteCustomCommand = async (commandId) => {
    try {
      await axios.delete(`${API}/api/voice/commands/${commandId}`, getAuthHeader());
      toast.success('Command deleted');
      fetchCustomCommands();
    } catch (error) {
      toast.error('Failed to delete command');
    }
  };

  const addTemplateCommand = async (templateName) => {
    setLoadingCommands(true);
    try {
      await axios.post(`${API}/api/voice/commands/add-template/${encodeURIComponent(templateName)}`, {}, getAuthHeader());
      toast.success('Template added to your commands!');
      fetchCustomCommands();
      fetchCommandTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add template');
    } finally {
      setLoadingCommands(false);
    }
  };

  // Build combined commands object (built-in + custom)
  const getAllCommands = () => {
    const combined = { ...VOICE_COMMANDS };
    customCommands.forEach(cmd => {
      combined[cmd.trigger_phrase] = cmd.output_text;
    });
    return combined;
  };

  // Process text for voice commands
  const processVoiceCommands = (text) => {
    let processedText = text;
    let commandFound = null;
    
    // Convert to lowercase for command matching
    const lowerText = text.toLowerCase().trim();
    
    // Get all commands including custom ones
    const allCommands = getAllCommands();
    
    // Check for commands (longest match first)
    const sortedCommands = Object.keys(allCommands).sort((a, b) => b.length - a.length);
    
    for (const command of sortedCommands) {
      if (lowerText.includes(command)) {
        // Replace the command with its formatted output
        const regex = new RegExp(command, 'gi');
        processedText = processedText.replace(regex, allCommands[command]);
        commandFound = command;
        break; // Only process one command at a time
      }
    }
    
    if (commandFound) {
      setLastCommand(commandFound);
      // Clear command indicator after 2 seconds
      setTimeout(() => setLastCommand(null), 2000);
    }
    
    return processedText;
  };

  const startSpeechRecognition = () => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.log('Speech recognition not supported in this browser');
      return;
    }

    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';

    recognitionRef.current.onresult = (event) => {
      let interim = '';
      let final = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          // Process voice commands in final results
          final += processVoiceCommands(transcript) + ' ';
        } else {
          interim += transcript;
        }
      }
      
      if (final) {
        setLiveTranscript(prev => prev + final);
      }
      setInterimTranscript(interim);
    };

    recognitionRef.current.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      // Don't show error for common issues like no-speech
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
      }
    };

    recognitionRef.current.onend = () => {
      // Restart if still recording (handles auto-stop after silence)
      if (isRecording && recognitionRef.current) {
        try {
          recognitionRef.current.start();
        } catch (e) {
          // Ignore errors when restarting
        }
      }
    };

    try {
      recognitionRef.current.start();
    } catch (e) {
      console.log('Could not start speech recognition:', e);
    }
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors when stopping
      }
      recognitionRef.current = null;
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      stopSpeechRecognition();
      setIsRecording(false);
      setInterimTranscript('');
      toast.success('Recording stopped');
    }
  };

  const clearVoiceRecording = () => {
    setAudioBlob(null);
    setVoiceResult(null);
    setLiveTranscript('');
    setInterimTranscript('');
    audioChunksRef.current = [];
  };

  const processVoiceToSOAP = async () => {
    if (!audioBlob) {
      toast.error('No recording to process');
      return;
    }
    if (!voicePatientId) {
      toast.error('Please select a patient');
      return;
    }

    setVoiceLoading(true);
    setVoiceResult(null);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const response = await axios.post(
          `${API}/api/voice/transcribe-to-soap`,
          {
            audio_data: base64Audio,
            audio_format: 'webm',
            patient_id: voicePatientId,
            note_title: voiceNoteTitle || null
          },
          getAuthHeader()
        );
        
        setVoiceResult(response.data);
        toast.success('Voice note converted to SOAP format!');
        fetchNotes(); // Refresh notes list
        setVoiceLoading(false);
      };
    } catch (error) {
      console.error('Voice to SOAP failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to process voice note');
      setVoiceLoading(false);
    }
  };

  const resetVoiceNote = () => {
    setAudioBlob(null);
    setVoiceResult(null);
    setVoicePatientId('');
    setVoiceNoteTitle('');
    setLiveTranscript('');
    setInterimTranscript('');
    setLastCommand(null);
    setShowCommandHelp(false);
    stopSpeechRecognition();
    audioChunksRef.current = [];
  };

  const filteredNotes = notes.filter(note => {
    if (selectedPatient && note.patient_id !== selectedPatient) return false;
    if (filterType !== 'all' && note.note_type !== filterType) return false;
    if (searchQuery && !note.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getNoteTypeInfo = (type) => NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[0];

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-cn-ink-900" data-testid="clinical-notes">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50 dark:border-cn-ink-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <CaraLoomLogo size={30} />
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-cn-ink-800 transition-colors"
              >
                {theme === 'light' ? <Moon className="w-5 h-5 text-slate-600" /> : <Sun className="w-5 h-5 text-yellow-400" />}
              </button>
              <Link to="/clinician/dashboard">
                <Button variant="outline" className="rounded-full">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-display">
              Clinical Notes
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Document and manage patient clinical records
            </p>
            {/* Iter 161 · Discoverability hint — surface AI SOAP entry
                point since it lives inside the New Note dialog. */}
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-cn-accent" />
              AI SOAP <span className="text-slate-400">·</span> click <span className="font-medium text-slate-700 dark:text-slate-300">New Note</span> → choose a SOAP type → the AI strengthen button lives inside.
              Or dictate straight to SOAP via the <span className="font-medium text-slate-700 dark:text-slate-300">Voice Note</span> button.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => { resetVoiceNote(); setShowVoiceNote(true); }}
              variant="outline"
              className="rounded-xl border-cn-sage-200 dark:border-cn-sage-800 hover:bg-cn-sage-50 dark:hover:bg-cn-sage-900/20"
              data-testid="voice-note-btn"
            >
              <Mic className="w-5 h-5 mr-2 text-cn-primary" />
              Voice Note
            </Button>
            <Button 
              onClick={() => setShowSummary(true)}
              variant="outline"
              className="rounded-xl border-cn-terracotta-200 dark:border-cn-terracotta-700 hover:bg-cn-terracotta-50 dark:hover:bg-cn-terracotta-700/20"
              data-testid="ai-summary-btn"
            >
              <Sparkles className="w-5 h-5 mr-2 text-cn-accent" />
              AI Summary
            </Button>
            <Button 
              onClick={() => { resetNewNote(); setShowNewNote(true); }}
              className="bg-gradient-to-r from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-xl"
              data-testid="new-note-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Note
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={selectedPatient || "all"} onValueChange={(val) => setSelectedPatient(val === "all" ? "" : val)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Patients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Patients</SelectItem>
                  {patients.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {NOTE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notes List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
              <CardContent className="py-12 text-center">
                <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin mx-auto" />
              </CardContent>
            </Card>
          ) : filteredNotes.length > 0 ? (
            filteredNotes.map(note => {
              const typeInfo = getNoteTypeInfo(note.note_type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <Card 
                  key={note.id} 
                  className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg hover:shadow-xl transition-shadow"
                  data-testid={`note-card-${note.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          note.note_type === 'incident' ? 'bg-red-100 dark:bg-red-900/30' :
                          note.note_type === 'soap' ? 'bg-cn-sage-100 dark:bg-cn-sage-900/30' :
                          note.note_type === 'progress' ? 'bg-cn-sage-100 dark:bg-cn-sage-900/30' :
                          'bg-slate-100 dark:bg-cn-ink-700'
                        }`}>
                          <TypeIcon className={`w-6 h-6 ${
                            note.note_type === 'incident' ? 'text-red-600' :
                            note.note_type === 'soap' ? 'text-cn-primary' :
                            note.note_type === 'progress' ? 'text-cn-primary' :
                            'text-slate-600'
                          }`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 dark:text-white">
                              {note.title}
                            </h3>
                            {note.is_locked && (
                              <Lock className="w-4 h-4 text-amber-500" />
                            )}
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-2">
                            <Badge variant="outline">{typeInfo.label}</Badge>
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {note.patient_name || 'Patient'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(note.created_at).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(note.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                          
                          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                            {note.content?.substring(0, 200)}...
                          </p>
                          
                          {note.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {note.tags.map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setViewingNote(note)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Create claim from note"
                          onClick={() => createClaimFromNote(note)}
                          disabled={creatingClaim === note.id}
                          data-testid={`create-claim-from-note-${note.id}`}
                        >
                          {creatingClaim === note.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4 text-cn-primary" />}
                        </Button>
                        {!note.is_locked && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditNote(note)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleLockNote(note.id, !note.is_locked)}
                        >
                          {note.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        </Button>
                        {!note.is_locked && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
              <CardContent className="py-16 text-center">
                <FileText className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  No Notes Found
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Start documenting patient care by creating your first clinical note.
                </p>
                <Button onClick={() => setShowNewNote(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Note
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* New/Edit Note Dialog */}
        <Dialog open={showNewNote} onOpenChange={(o) => { setShowNewNote(o); if (!o) setStrengthenBackup(null); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingNote ? 'Edit Clinical Note' : 'New Clinical Note'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Note Type Selection */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {NOTE_TYPES.slice(0, 4).map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setNewNote(prev => ({ ...prev, note_type: type.value }))}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        newNote.note_type === type.value 
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/20' 
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-1 ${
                        newNote.note_type === type.value ? 'text-cn-primary' : 'text-slate-400'
                      }`} />
                      <div className="text-sm font-medium">{type.label}</div>
                    </button>
                  );
                })}
              </div>

              {/* Patient & Title */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Patient *
                  </label>
                  <Select
                    value={newNote.patient_id}
                    onValueChange={(val) => setNewNote(prev => ({ ...prev, patient_id: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.first_name} {p.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Note Title *
                  </label>
                  <Input
                    value={newNote.title}
                    onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="E.g., Follow-up Visit - Diabetes Management"
                  />
                </div>
              </div>

              {/* SOAP Note Fields */}
              {newNote.note_type === 'soap' && (
                <div className="space-y-4">
                  <div className="bg-cn-sage-50 dark:bg-cn-sage-900/20 p-4 rounded-xl">
                    <h3 className="font-semibold text-cn-sage-900 dark:text-cn-sage-300 mb-3 flex items-center gap-2">
                      <ClipboardList className="w-5 h-5" />
                      SOAP Documentation
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-cn-primary text-white text-xs flex items-center justify-center">S</span>
                          Subjective
                        </label>
                        <Textarea
                          value={newNote.soap.subjective}
                          onChange={(e) => setNewNote(prev => ({ 
                            ...prev, 
                            soap: { ...prev.soap, subjective: e.target.value } 
                          }))}
                          placeholder="Patient's symptoms, complaints, history in their own words..."
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-cn-primary text-white text-xs flex items-center justify-center">O</span>
                          Objective
                        </label>
                        <Textarea
                          value={newNote.soap.objective}
                          onChange={(e) => setNewNote(prev => ({ 
                            ...prev, 
                            soap: { ...prev.soap, objective: e.target.value } 
                          }))}
                          placeholder="Physical exam findings, vital signs, test results..."
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-xs flex items-center justify-center">A</span>
                          Assessment
                        </label>
                        <Textarea
                          value={newNote.soap.assessment}
                          onChange={(e) => setNewNote(prev => ({ 
                            ...prev, 
                            soap: { ...prev.soap, assessment: e.target.value } 
                          }))}
                          placeholder="Diagnosis, clinical impression, interpretation of findings..."
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-cn-accent text-white text-xs flex items-center justify-center">P</span>
                          Plan
                        </label>
                        <Textarea
                          value={newNote.soap.plan}
                          onChange={(e) => setNewNote(prev => ({ 
                            ...prev, 
                            soap: { ...prev.soap, plan: e.target.value } 
                          }))}
                          placeholder="Treatment plan, medications, follow-up instructions..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                  <DocStrengthMeter soap={newNote.soap} noteType={newNote.note_type} onApply={applyStrengthened} canUndo={!!strengthenBackup} onUndo={undoStrengthen} />
                </div>
              )}

              {/* General Content for non-SOAP notes */}
              {newNote.note_type !== 'soap' && (
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Note Content
                  </label>
                  <Textarea
                    value={newNote.content}
                    onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter clinical documentation..."
                    rows={8}
                  />
                  <DocStrengthMeter content={newNote.content} noteType={newNote.note_type} onApply={applyStrengthened} canUndo={!!strengthenBackup} onUndo={undoStrengthen} />
                </div>
              )}

              {/* Vitals Section */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  Vital Signs (Optional)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Input
                    placeholder="BP (e.g., 120/80)"
                    value={newNote.vitals.blood_pressure}
                    onChange={(e) => setNewNote(prev => ({ 
                      ...prev, 
                      vitals: { ...prev.vitals, blood_pressure: e.target.value } 
                    }))}
                  />
                  <Input
                    placeholder="HR (bpm)"
                    value={newNote.vitals.heart_rate}
                    onChange={(e) => setNewNote(prev => ({ 
                      ...prev, 
                      vitals: { ...prev.vitals, heart_rate: e.target.value } 
                    }))}
                  />
                  <Input
                    placeholder="Temp (°F)"
                    value={newNote.vitals.temperature}
                    onChange={(e) => setNewNote(prev => ({ 
                      ...prev, 
                      vitals: { ...prev.vitals, temperature: e.target.value } 
                    }))}
                  />
                  <Input
                    placeholder="O2 Sat (%)"
                    value={newNote.vitals.oxygen_saturation}
                    onChange={(e) => setNewNote(prev => ({ 
                      ...prev, 
                      vitals: { ...prev.vitals, oxygen_saturation: e.target.value } 
                    }))}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowNewNote(false); setEditingNote(null); }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveNote}
                  disabled={loading}
                  className="bg-gradient-to-r from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingNote ? 'Update Note' : 'Save Note'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Note Dialog */}
        <Dialog open={!!viewingNote} onOpenChange={() => setViewingNote(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {viewingNote && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {viewingNote.title}
                    {viewingNote.is_locked && <Lock className="w-4 h-4 text-amber-500" />}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="flex flex-wrap gap-2">
                    <Badge>{getNoteTypeInfo(viewingNote.note_type).label}</Badge>
                    <Badge variant="outline">
                      {new Date(viewingNote.created_at).toLocaleString()}
                    </Badge>
                  </div>

                  {/* Vitals */}
                  {viewingNote.vitals && Object.values(viewingNote.vitals).some(v => v) && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl">
                      <h4 className="font-semibold text-red-900 dark:text-red-300 mb-2 flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        Vital Signs
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        {viewingNote.vitals.blood_pressure && (
                          <div>
                            <span className="text-slate-500">BP:</span> {viewingNote.vitals.blood_pressure}
                          </div>
                        )}
                        {viewingNote.vitals.heart_rate && (
                          <div>
                            <span className="text-slate-500">HR:</span> {viewingNote.vitals.heart_rate} bpm
                          </div>
                        )}
                        {viewingNote.vitals.temperature && (
                          <div>
                            <span className="text-slate-500">Temp:</span> {viewingNote.vitals.temperature}°F
                          </div>
                        )}
                        {viewingNote.vitals.oxygen_saturation && (
                          <div>
                            <span className="text-slate-500">O2:</span> {viewingNote.vitals.oxygen_saturation}%
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="prose dark:prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-sm bg-slate-50 dark:bg-cn-ink-800 p-4 rounded-xl">
                      {viewingNote.content}
                    </pre>
                  </div>

                  {/* Tags */}
                  {viewingNote.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {viewingNote.tags.map(tag => (
                        <Badge key={tag} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  )}

                  {/* Create claim from note (auto-coding) */}
                  <div className="pt-3 border-t border-cn-border">
                    <Button
                      onClick={() => createClaimFromNote(viewingNote)}
                      disabled={creatingClaim === viewingNote.id}
                      className="cn-btn cn-btn-primary !py-2"
                      data-testid="create-claim-from-note-dialog-btn"
                    >
                      {creatingClaim === viewingNote.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                      Create claim from this note
                    </Button>
                    <p className="mt-1.5 text-xs text-cn-text-muted">AI codes the best-supported CPT + diagnoses into a draft claim you review before submitting.</p>
                  </div>
                </div>

                {/* CPT + RVU suggestions panel (Blueprint #23) */}
                <CptSuggestionsPanel noteId={viewingNote.id} getAuthHeader={getAuthHeader} />
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* AI Summary Dialog */}
        <Dialog open={showSummary} onOpenChange={setShowSummary}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cn-accent" />
                AI Clinical Summary Generator
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Patient Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Select Patient *
                </label>
                <Select
                  value={selectedPatient || "none"}
                  onValueChange={(val) => setSelectedPatient(val === "none" ? "" : val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a patient" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a patient...</SelectItem>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Summary Type Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Summary Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'handoff', label: 'Handoff Summary', icon: RefreshCw, desc: 'For shift handoffs' },
                    { value: 'shift_change', label: 'Shift Report', icon: Clock, desc: 'End of shift report' },
                    { value: 'care_plan', label: 'Care Plan', icon: ClipboardList, desc: 'Care coordination' },
                    { value: 'weekly', label: 'Weekly Summary', icon: Calendar, desc: 'Weekly progress' }
                  ].map(type => (
                    <button
                      key={type.value}
                      onClick={() => setSummaryType(type.value)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        summaryType === type.value 
                          ? 'border-cn-accent bg-cn-terracotta-50 dark:bg-cn-terracotta-700/20' 
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 mb-1 ${
                        summaryType === type.value ? 'text-cn-accent' : 'text-slate-400'
                      }`} />
                      <div className="text-sm font-medium">{type.label}</div>
                      <div className="text-xs text-slate-500">{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={generateSummary}
                disabled={summaryLoading || !selectedPatient}
                className="w-full bg-gradient-to-r from-cn-accent to-cn-sage-600 hover:from-cn-accent hover:to-cn-sage-600 text-white rounded-xl h-12"
                data-testid="generate-summary-btn"
              >
                {summaryLoading ? (
                  <>Generating AI Summary...</>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Summary
                  </>
                )}
              </Button>

              {/* Loading State */}
              {summaryLoading && (
                <AIBrainLoader message="AI is analyzing clinical notes..." />
              )}

              {/* Summary Result */}
              {summaryResult && !summaryLoading && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-cn-terracotta-100 text-cn-terracotta-600 dark:bg-cn-terracotta-700/30 dark:text-cn-terracotta-300">
                        {summaryType.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <span className="text-sm text-slate-500">
                        {summaryResult.notes_summarized} notes summarized
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copySummary}
                      className="rounded-lg"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>

                  <div className="bg-gradient-to-br from-cn-terracotta-50 to-cn-sage-50 dark:from-cn-ink-800 dark:to-cn-ink-900 p-6 rounded-xl border border-cn-terracotta-200 dark:border-cn-terracotta-700">
                    <div className="prose dark:prose-invert max-w-none">
                      <div className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                        {summaryResult.summary}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Patient: {summaryResult.patient_name}</span>
                    <span>Generated: {new Date(summaryResult.generated_at).toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="bg-cn-sage-50 dark:bg-cn-sage-900/20 p-4 rounded-xl text-sm text-cn-primary-hover dark:text-cn-sage-300">
                <p className="flex items-start gap-2">
                  <Brain className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    AI summaries are generated from clinical notes recorded in the last 7 days. 
                    Always verify AI-generated content before clinical use.
                  </span>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Voice Note Dialog */}
        <Dialog open={showVoiceNote} onOpenChange={setShowVoiceNote}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-cn-primary" />
                Voice-to-SOAP Note
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Patient Selection */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Select Patient *
                </label>
                <Select
                  value={voicePatientId || "none"}
                  onValueChange={(val) => setVoicePatientId(val === "none" ? "" : val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a patient" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a patient...</SelectItem>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Optional Title */}
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Note Title (optional)
                </label>
                <Input
                  placeholder="Leave blank for AI-suggested title"
                  value={voiceNoteTitle}
                  onChange={(e) => setVoiceNoteTitle(e.target.value)}
                />
              </div>

              {/* Voice Commands Help Toggle */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowCommandHelp(!showCommandHelp)}
                  className="flex items-center gap-2 text-sm text-cn-primary dark:text-cn-sage-300 hover:text-cn-sage-700 dark:hover:text-cn-sage-300 transition-colors"
                >
                  <Command className="w-4 h-4" />
                  {showCommandHelp ? 'Hide' : 'Show'} Voice Commands
                  <HelpCircle className="w-4 h-4" />
                </button>
                {lastCommand && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full animate-pulse">
                    <Zap className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-medium text-green-700 dark:text-green-400">
                      "{lastCommand}" recognized!
                    </span>
                  </div>
                )}
              </div>

              {/* Voice Commands Help Panel */}
              {showCommandHelp && (
                <div className="bg-gradient-to-br from-slate-100 to-slate-50 dark:from-cn-ink-800 dark:to-cn-ink-900 rounded-xl p-4 border border-slate-200 dark:border-cn-ink-700">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <Command className="w-4 h-4 text-cn-primary" />
                    Available Voice Commands
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="font-medium text-cn-sage-700 dark:text-cn-sage-300 mb-2">📋 SOAP Sections:</p>
                      <ul className="space-y-1 text-slate-600 dark:text-slate-400">
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">new section subjective</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">section objective</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">start assessment</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">section plan</span>"</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-cn-sage-700 dark:text-cn-sage-300 mb-2">✏️ Formatting:</p>
                      <ul className="space-y-1 text-slate-600 dark:text-slate-400">
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">new paragraph</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">new line</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">bullet point</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">period</span>" / "<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">comma</span>"</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-cn-sage-700 dark:text-cn-sage-300 mb-2">🏥 Medical Shortcuts:</p>
                      <ul className="space-y-1 text-slate-600 dark:text-slate-400">
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">vitals colon</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">diagnosis colon</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">chief complaint</span>"</li>
                        <li>"<span className="font-mono bg-slate-200 dark:bg-cn-ink-700 px-1 rounded">patient reports</span>"</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-cn-sage-700 dark:text-cn-sage-300 mb-2">💡 Tips:</p>
                      <ul className="space-y-1 text-slate-600 dark:text-slate-400">
                        <li>Speak commands clearly</li>
                        <li>Slight pause before command</li>
                        <li>Green flash = recognized</li>
                        <li>Commands auto-format text</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Commands Management Button */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCustomCommands(!showCustomCommands)}
                  className="flex items-center gap-2 text-sm text-cn-accent dark:text-cn-terracotta-300 hover:text-cn-terracotta-600 dark:hover:text-cn-terracotta-300 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  {showCustomCommands ? 'Hide' : 'Manage'} Custom Commands
                  {customCommands.length > 0 && (
                    <span className="px-2 py-0.5 bg-cn-terracotta-100 dark:bg-cn-terracotta-700/30 text-cn-terracotta-600 dark:text-cn-terracotta-300 rounded-full text-xs">
                      {customCommands.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Custom Commands Panel */}
              {showCustomCommands && (
                <div className="bg-gradient-to-br from-cn-terracotta-50 to-cn-sage-50 dark:from-cn-terracotta-700/20 dark:to-cn-sage-900/20 rounded-xl p-4 border border-cn-terracotta-200 dark:border-cn-terracotta-700 space-y-4">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Layers className="w-4 h-4 text-cn-accent" />
                    Custom Voice Commands
                  </h4>

                  {/* Pre-built Templates */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-cn-terracotta-600 dark:text-cn-terracotta-300">📦 Quick-add Templates:</p>
                    <div className="flex flex-wrap gap-2">
                      {commandTemplates.filter(t => !t.already_added).slice(0, 4).map(template => (
                        <button
                          key={template.name}
                          onClick={() => addTemplateCommand(template.name)}
                          disabled={loadingCommands}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-cn-ink-800 rounded-lg border border-cn-terracotta-200 dark:border-cn-terracotta-700 text-xs hover:bg-cn-terracotta-50 dark:hover:bg-cn-terracotta-700/30 transition-colors"
                          title={template.description}
                        >
                          <PlusCircle className="w-3 h-3 text-cn-accent" />
                          {template.name}
                        </button>
                      ))}
                      {commandTemplates.filter(t => !t.already_added).length === 0 && (
                        <span className="text-xs text-slate-500">All templates added!</span>
                      )}
                    </div>
                  </div>

                  {/* Your Custom Commands */}
                  {customCommands.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-cn-terracotta-600 dark:text-cn-terracotta-300">🎯 Your Commands:</p>
                      <div className="space-y-2 max-h-[150px] overflow-y-auto">
                        {customCommands.map(cmd => (
                          <div 
                            key={cmd.id}
                            className="flex items-center justify-between p-2 bg-white dark:bg-cn-ink-800 rounded-lg border border-slate-200 dark:border-cn-ink-700"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <p className="text-xs font-mono text-cn-accent dark:text-cn-terracotta-300 truncate">
                                  "{cmd.trigger_phrase}"
                                </p>
                              </div>
                              <p className="text-xs text-slate-500 truncate">
                                {cmd.description || cmd.output_text.substring(0, 50) + '...'}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <span className="text-xs text-slate-400">{cmd.usage_count}x</span>
                              <button
                                onClick={() => deleteCustomCommand(cmd.id)}
                                className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}


                  {/* Create New Command */}
                  <div className="space-y-2 pt-2 border-t border-cn-terracotta-200 dark:border-cn-terracotta-700">
                    <p className="text-xs font-medium text-cn-terracotta-600 dark:text-cn-terracotta-300">➕ Create New Command:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        placeholder="Trigger phrase (e.g., 'my vitals')"
                        value={newCustomCommand.trigger_phrase}
                        onChange={(e) => setNewCustomCommand({...newCustomCommand, trigger_phrase: e.target.value})}
                        className="text-xs h-8"
                      />
                      <Input
                        placeholder="Description (optional)"
                        value={newCustomCommand.description}
                        onChange={(e) => setNewCustomCommand({...newCustomCommand, description: e.target.value})}
                        className="text-xs h-8"
                      />
                    </div>
                    <Textarea
                      placeholder="Output text (what gets inserted when you say the trigger)"
                      value={newCustomCommand.output_text}
                      onChange={(e) => setNewCustomCommand({...newCustomCommand, output_text: e.target.value})}
                      className="text-xs min-h-[60px]"
                    />
                    <Button
                      onClick={createCustomCommand}
                      disabled={loadingCommands || !newCustomCommand.trigger_phrase || !newCustomCommand.output_text}
                      className="w-full h-8 text-xs bg-cn-accent hover:bg-cn-terracotta-600 text-white rounded-lg"
                    >
                      <PlusCircle className="w-3 h-3 mr-1" />
                      Create Command
                    </Button>
                  </div>
                </div>
              )}

              {/* Recording Section */}
              <div className="bg-slate-50 dark:bg-cn-ink-800 rounded-xl p-6">
                <div className="flex flex-col items-center">
                  <button
                    onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                    disabled={voiceLoading}
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all transform hover:scale-105 ${
                      isRecording
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                        : 'bg-gradient-to-br from-cn-primary to-cn-sage-600 hover:from-cn-sage-600 hover:to-cn-sage-700'
                    } shadow-lg`}
                    data-testid="voice-record-button"
                  >
                    {isRecording ? (
                      <Square className="w-8 h-8 text-white" />
                    ) : (
                      <Mic className="w-8 h-8 text-white" />
                    )}
                  </button>
                  <p className="text-slate-600 dark:text-slate-400 mt-4 text-center">
                    {isRecording 
                      ? 'Recording... Click to stop' 
                      : 'Click to start recording your clinical notes'}
                  </p>
                  {!isRecording && !liveTranscript && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Speak naturally. Use voice commands like "new section subjective" to format.
                    </p>
                  )}
                </div>

                {/* Live Transcript Preview */}
                {(isRecording || liveTranscript) && (
                  <div className="mt-6 p-4 bg-gradient-to-br from-cn-sage-50 to-cn-sage-50 dark:from-cn-sage-900/20 dark:to-cn-sage-900/20 rounded-xl border border-cn-sage-200 dark:border-cn-sage-800">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-2 h-2 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-cn-primary'}`} />
                      <span className="text-sm font-medium text-cn-sage-700 dark:text-cn-sage-300">
                        {isRecording ? 'Live Preview' : 'Transcript Preview'}
                      </span>
                      {isRecording && (
                        <span className="text-xs text-slate-500 ml-auto flex items-center gap-1">
                          <Command className="w-3 h-3" />
                          Voice commands active
                        </span>
                      )}
                    </div>
                    <div className="min-h-[80px] max-h-[200px] overflow-y-auto">
                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                        {liveTranscript}
                        <span className="text-cn-primary dark:text-cn-sage-300 italic">
                          {interimTranscript}
                        </span>
                        {isRecording && !liveTranscript && !interimTranscript && (
                          <span className="text-slate-400 italic">Start speaking to see live transcription...</span>
                        )}
                      </p>
                    </div>
                    {liveTranscript && !isRecording && (
                      <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-cn-sage-200 dark:border-cn-sage-700">
                        💡 This is a preview. Whisper AI will provide the final accurate transcription when you process.
                      </p>
                    )}
                  </div>
                )}

                {/* Audio Preview */}
                {audioBlob && !voiceLoading && (
                  <div className="mt-6 p-4 bg-white dark:bg-cn-ink-700 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-5 h-5 text-cn-primary" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          Recording Ready
                        </span>
                      </div>
                      <button
                        onClick={clearVoiceRecording}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <audio controls className="w-full" src={URL.createObjectURL(audioBlob)} />
                  </div>
                )}
              </div>

              {/* Process Button */}
              {audioBlob && !voiceResult && (
                <Button
                  onClick={processVoiceToSOAP}
                  disabled={voiceLoading || !voicePatientId}
                  className="w-full bg-gradient-to-r from-cn-primary to-cn-sage-600 hover:from-cn-sage-600 hover:to-cn-sage-700 text-white rounded-xl h-12"
                  data-testid="process-voice-btn"
                >
                  {voiceLoading ? (
                    <>Processing with AI...</>
                  ) : (
                    <>
                      <Brain className="w-5 h-5 mr-2" />
                      Convert to SOAP Note
                    </>
                  )}
                </Button>
              )}

              {/* Loading State */}
              {voiceLoading && (
                <AIBrainLoader message="Transcribing and structuring your clinical notes..." />
              )}

              {/* Result */}
              {voiceResult && !voiceLoading && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-700 dark:text-green-400">
                      SOAP Note Created Successfully!
                    </span>
                  </div>

                  {/* Raw Transcription */}
                  <div className="bg-slate-100 dark:bg-cn-ink-800 p-4 rounded-xl">
                    <p className="text-xs text-slate-500 mb-2">Raw Transcription:</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 italic">
                      "{voiceResult.raw_transcription}"
                    </p>
                  </div>

                  {/* SOAP Structure */}
                  <div className="bg-gradient-to-br from-cn-sage-50 to-cn-sage-50 dark:from-cn-ink-800 dark:to-cn-ink-900 p-6 rounded-xl border border-cn-sage-200 dark:border-cn-sage-800">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-4">
                      {voiceResult.structured_note?.title}
                    </h4>
                    
                    <div className="space-y-4">
                      {['subjective', 'objective', 'assessment', 'plan'].map(section => (
                        <div key={section}>
                          <p className="text-xs font-bold text-cn-sage-700 dark:text-cn-sage-300 uppercase mb-1">
                            {section}
                          </p>
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {voiceResult.structured_note?.soap?.[section] || 'Not documented'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setShowVoiceNote(false);
                        resetVoiceNote();
                      }}
                      className="flex-1 bg-gradient-to-r from-cn-primary to-cn-sage-500 text-white rounded-xl"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Done
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetVoiceNote}
                      className="rounded-xl"
                    >
                      Record Another
                    </Button>
                  </div>
                </div>
              )}

              {/* Info */}
              <div className="bg-cn-sage-50 dark:bg-cn-sage-900/20 p-4 rounded-xl text-sm text-cn-sage-700 dark:text-cn-sage-300">
                <p className="flex items-start gap-2">
                  <Mic className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>
                    Speak your clinical notes naturally. AI will transcribe using Whisper and structure into SOAP format.
                    The note will be saved automatically.
                  </span>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </main>
    </div>
  );
}
